import React, { useEffect, useState, useCallback } from "react"
import DataTable, { TableColumn } from "react-data-table-component"
import { Button, Alert, Badge, Spinner } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import useFetch from "../../../_fetch"

interface PublisherUser {
    username: string
    userId: string
    addedAt: string
    active: boolean
}

interface Advertiser {
    username: string
    userId: string
    addedAt: string
}

interface Partnership {
    id: string
    name: string
    type: string
    mode: string
    value: number
    currency: string
    startDate: string
    expiryDate: string | null
    cookieDays: number
    createdAt: string
    domains: string[]
    publisher_users: PublisherUser[]
    advertiser?: Advertiser
}

const PartnershipAgreements: React.FC = () => {
    const { doFetch } = useFetch()
    const navigate = useNavigate()
    const [partnerships, setPartnerships] = useState<Partnership[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchPartnerships = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await doFetch("/user/admin/partnerships")
            if (!res.ok) throw new Error(await res.text())
            const data = await res.json()
            setPartnerships(data)
        } catch (err: any) {
            setError(err?.message || "Failed to load partnerships")
        } finally {
            setLoading(false)
        }
    }, [doFetch])

    useEffect(() => {
        fetchPartnerships()
    }, [])

    const formatValue = (row: Partnership) => {
        if (row.type === "sale") return `${row.value}%`
        return `${row.value} ${row.currency}`
    }

    const typeBadgeVariant = (type: string) => {
        if (type === "sale") return "success"
        if (type === "click") return "primary"
        if (type === "lead") return "warning"
        return "secondary"
    }

    const columns: TableColumn<Partnership>[] = [
        {
            name: "ID",
            selector: (row) => row.id,
            sortable: true,
            width: "140px",
            cell: (row) => <code style={{ fontSize: "0.78rem" }}>{row.id}</code>,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            grow: 1.5,
        },
        {
            name: "Type",
            selector: (row) => row.type,
            sortable: true,
            width: "90px",
            cell: (row) => (
                <Badge bg={typeBadgeVariant(row.type)} style={{ fontSize: "0.75rem" }}>
                    {row.type}
                </Badge>
            ),
        },
        {
            name: "Mode",
            selector: (row) => row.mode,
            sortable: true,
            width: "80px",
            cell: (row) => (
                <Badge bg={row.mode === "server" ? "dark" : "secondary"} style={{ fontSize: "0.75rem" }}>
                    {row.mode}
                </Badge>
            ),
        },
        {
            name: "Value",
            selector: (row) => row.value,
            sortable: true,
            width: "100px",
            cell: (row) => <span style={{ display: "block", textAlign: "right", width: "100%" }}>{formatValue(row)}</span>,
        },
        {
            name: "Start",
            selector: (row) => row.startDate,
            sortable: true,
            width: "100px",
            cell: (row) => <span style={{ whiteSpace: "nowrap" }}>{row.startDate}</span>,
        },
        {
            name: "Expires",
            selector: (row) => row.expiryDate ?? "",
            sortable: true,
            width: "100px",
            cell: (row) => row.expiryDate
                ? <span style={{ whiteSpace: "nowrap" }}>{row.expiryDate}</span>
                : <span className="text-muted">—</span>,
        },
        {
            name: "Cookie",
            selector: (row) => row.cookieDays,
            sortable: true,
            width: "80px",
            cell: (row) =>
                row.type === "click" ? (
                    <span className="text-muted" style={{ display: "block", textAlign: "right", width: "100%" }}>—</span>
                ) : (
                    <span style={{ display: "block", textAlign: "right", width: "100%" }}>{row.cookieDays}d</span>
                ),
        },
        {
            name: "Publishers",
            grow: 1.5,
            cell: (row) => (
                <div style={{ lineHeight: 1.4, padding: "4px 0", fontSize: "0.82rem" }}>
                    {row.publisher_users.length === 0 ? (
                        <span className="text-muted">—</span>
                    ) : (
                        row.publisher_users.map((p) => (
                            <div key={p.userId}>
                                <span
                                    style={{
                                        fontSize: "0.72rem",
                                        display: "inline-block",
                                        padding: "1px 6px",
                                        borderRadius: "4px",
                                        background: p.active ? "#e9ecef" : "#f8f9fa",
                                        color: p.active ? "#343a40" : "#adb5bd",
                                        border: "1px solid #ced4da",
                                    }}
                                >
                                    {p.username}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            ),
        },
        {
            name: "Advertiser",
            grow: 1,
            cell: (row) =>
                row.advertiser ? (
                    <span
                        style={{
                            fontSize: "0.75rem",
                            display: "inline-block",
                            padding: "2px 7px",
                            borderRadius: "4px",
                            background: "#343a40",
                            color: "#fff",
                        }}
                    >
                        {row.advertiser.username}
                    </span>
                ) : (
                    <span className="text-muted" style={{ fontSize: "0.82rem" }}>—</span>
                ),
        },
        {
            name: "Created",
            selector: (row) => row.createdAt,
            sortable: true,
            id: "createdAt",
            width: "110px",
            cell: (row) => <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>{row.createdAt.slice(0, 10)}</span>,
        },
        {
            name: "",
            width: "80px",
            cell: (row) => (
                <Button
                    size="sm"
                    variant="outline-primary"
                    style={{ fontSize: "0.75rem", padding: 0, whiteSpace: "nowrap" }}
                    onClick={() => navigate(`/partnership-agreements/${row.id}`)}
                >
                    View
                </Button>
            ),
        },
    ]

    return (
        <div className="p-3">
            <h4 className="mb-3">Partnership Agreements</h4>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <DataTable
                columns={columns}
                data={partnerships}
                progressPending={loading}
                progressComponent={<Spinner animation="border" size="sm" className="m-3" />}
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                defaultSortFieldId="createdAt"
                defaultSortAsc={false}
                noDataComponent={<div className="p-3 text-muted">No partnership agreements found.</div>}
                dense
            />


        </div>
    )
}

export default PartnershipAgreements
